import { CompactInlineAlertWidget, DropdownButtonType, InlineAlertType, List, ListItem, SingleSelectDropdown } from '@tableau/widgets';
import React, { useState } from 'react';

import { Messages } from '~src/codegen/Localize';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { EditConnectionDialogProps, EditConnectionMultiRowState, EditConnectionRowProps, PerConnectionFieldState } from '~src/connectivity/components/EditConnection/EditConnectionAction';
import { EditConnectionReactActionUtils } from '~src/connectivity/components/EditConnection/utils/EditConnectionActionUtils';

import { EditConnectionDialogBridgeInfo } from './EditConnectionActionDialogBridgeInfo';
import { EditConnectionDialogEmbeddedPasswordInput, EditConnectionDialogEmbeddedToggle } from './EditConnectionActionDialogEmbeddedToggle';
import { EditConnectionDialogPrompt } from './EditConnectionActionDialogPrompt';
import { EditConnectionDialogTestConnection } from './EditConnectionActionDialogTestConnection';
import { EditConnectionDialogValueRow } from './EditConnectionActionDialogValueRow';

export type EditConnectionDialogBodyReactActionProps = ReactActionDialogBodyProps<
  EditConnectionDialogProps,
  EditConnectionMultiRowState
>;

// W-22483230: same validity rule the Save gate uses (port in range + consistent WIF state),
// computed per row so the body can flag a connection that blocks Save even when it is not on screen.
function isRowValid(rowState: PerConnectionFieldState): boolean {
  return EditConnectionReactActionUtils.isPortValid(rowState.serverPort)
    && EditConnectionReactActionUtils.isWifStateValid(rowState);
}

export function EditConnectionActionDialogBody(props: EditConnectionDialogBodyReactActionProps) {
  // The dropdown selection is local view state, not a staged edit: every row's edits live in
  // props.dialogState.rows, so switching the selection never discards a hidden connection's edits.
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Replace one row immutably, located by connectionId so updates never depend on list position.
  function updateRowState<K extends keyof PerConnectionFieldState>(
    connectionId: string,
    field: K,
    value: PerConnectionFieldState[K]
  ) {
    const rows = props.dialogState.rows.map((row) =>
      row.connectionId === connectionId ? { ...row, [field]: value } : row
    );
    props.updateDialogState({ rows });
  }

  // A fixed-size dialog: the selector plus exactly one connection editor, however many connections.
  // The router guarantees at least one connection today; guard anyway so an empty selection cannot
  // deref props.rows[-1] (Math.min with rows.length-1 would yield -1).
  if (props.rows.length === 0) {
    return null;
  }
  const selected = Math.min(selectedIndex, props.rows.length - 1);
  const showSelector = props.rows.length > 1;

  // Save is gated on every row, including hidden ones. Surface which hidden connection blocks Save so
  // the disabled button is not a mystery: mark invalid options and point to an off-screen offender.
  const rowValidity = props.dialogState.rows.map((rowState) => isRowValid(rowState));
  const hasHiddenInvalidRow = rowValidity.some((valid, index) => !valid && index !== selected);

  // Key options by index, not rowLabel, so two connections sharing a caption do not collide.
  const selectorItems: ListItem[] = props.rows.map((rowProps, index) => ({
    content: rowValidity[index]
      ? rowProps.rowLabel
      : rowProps.rowLabel + ' ' + Messages.editConnection_connectionSelector_needsAttention(),
    value: String(index)
  }));
  const selectedValue = String(selected);

  return (
    <>
      <EditConnectionDialogPrompt prompt={props.prompt} />
      {/* W-19373381: client-side password encryption was removed from this flow; the password is
          now sent plaintext over TLS. On insecure (HTTP) installs this surfaces the Security-required
          warning that credentials are transmitted unprotected. Gated by a flag so the warning can be
          rolled out independently of the fix. */}
      {FeatureFlags.insecureConnectionCredentialWarningEnabled && window.isSecureContext === false ? (
        <CompactInlineAlertWidget
          children={Messages.insecureConnectionCredentialWarning()}
          type={InlineAlertType.Warning}
          testId={'edit-conn-insecure-warning'}
        />
      ) : null}
      {showSelector ? (
        <SingleSelectDropdown
          style={{ width: '100%' }}
          value={selectedValue}
          onChange={(value: string) => setSelectedIndex(Number(value))}
          testId={'edit-conn-connection-selector'}
          data-tb-test-id={'edit-conn-connection-selector'}
          list={<List items={selectorItems} selectedValue={selectedValue} />}
          label={Messages.editConnection_connectionSelector_label()}
          type={DropdownButtonType.Outline}
        />
      ) : null}
      {showSelector && hasHiddenInvalidRow ? (
        <CompactInlineAlertWidget
          children={Messages.editConnection_connectionSelector_invalidHint()}
          type={InlineAlertType.Warning}
          testId={'edit-conn-hidden-invalid-hint'}
        />
      ) : null}
      <ConnectionEditRow
        key={props.rows[selected].connectionId}
        rowProps={props.rows[selected]}
        rowState={props.dialogState.rows[selected]}
        updateRowState={updateRowState}
      />
    </>
  );
}

type ConnectionEditRowProps = Readonly<{
  rowProps: EditConnectionRowProps;
  rowState: PerConnectionFieldState;
  updateRowState: <K extends keyof PerConnectionFieldState>(
    connectionId: string,
    field: K,
    value: PerConnectionFieldState[K]
  ) => void;
}>;

// W-22483230: renders one connection's fields. Per-field test ids are suffixed with the connection
// id so two rows in the same dialog do not collide.
export function ConnectionEditRow(props: ConnectionEditRowProps) {
  const { rowProps, rowState } = props;
  const id = rowProps.connectionId;
  const suffix = '-' + id;
  const update = <K extends keyof PerConnectionFieldState>(field: K, value: PerConnectionFieldState[K]) =>
    props.updateRowState(id, field, value);
  const showEmbeddedPasswordToggle = rowProps.isEmbeddedToggleVisible;

  return (
    <div style={{ width: '100%' }} data-tb-test-id={'edit-conn-row' + suffix}>
      <EditConnectionDialogPrompt prompt={rowProps.rowLabel} />

      {rowProps.isServerNameVisible ? (
        <EditConnectionDialogValueRow
          labelText={Messages.serverName()}
          valueText={rowState.serverName}
          isDisabled={rowProps.isServerNameDisabled}
          testId={'edit-conn-server-name' + suffix}
          maxLength={255}
          handleChange={(value) => update('serverName', value)}
        />
      ) : null}

      {rowProps.isServerPortVisible ? (
        <EditConnectionDialogValueRow
          labelText={Messages.serverPort()}
          valueText={rowState.serverPort}
          isDisabled={rowProps.isServerPortDisabled}
          isValid={EditConnectionReactActionUtils.isPortValid(rowState.serverPort)}
          testId={'edit-conn-server-port' + suffix}
          handleChange={(value) => update('serverPort', value)}
        />
      ) : null}

      {rowProps.isUsernameVisible ? (
        <EditConnectionDialogValueRow
          labelText={Messages.username()}
          valueText={rowState.username}
          isDisabled={false}
          testId={'edit-conn-username' + suffix}
          maxLength={255}
          handleChange={(value) => update('username', value)}
        />
      ) : null}

      {showEmbeddedPasswordToggle ? (
        <EditConnectionDialogEmbeddedToggle
          isPasswordEmbedded={rowState.isPasswordEmbedded}
          handleToggleChange={(value) => update('isPasswordEmbedded', value)}
          password={rowState.newPassword}
          handlePasswordChange={(value) => update('newPassword', value)}
        />
      ) : null}

      {showEmbeddedPasswordToggle && rowState.isPasswordEmbedded ? (
        <EditConnectionDialogEmbeddedPasswordInput
          isPasswordEmbedded={rowState.isPasswordEmbedded}
          handleToggleChange={(value) => update('isPasswordEmbedded', value)}
          password={rowState.newPassword}
          handlePasswordChange={(value) => update('newPassword', value)}
        />
      ) : null}

      {rowProps.isTestConnectionButtonVisible ? (
        <EditConnectionDialogTestConnection
          dialogState={rowState}
          onTestConnection={rowProps.onTestConnection}
        />
      ) : null}

      <EditConnectionDialogBridgeInfo
        usingRemoteQueryAgent={rowState.usingRemoteQueryAgent}
        handleUsingRemoteQueryAgentChange={(value) => update('usingRemoteQueryAgent', value)}
        showNetworkType={rowProps.showNetworkType}
      />
    </div>
  );
}
